"use client";
import { Header } from '@/pages/home/sections/header';
import { Footer } from '@/pages/home/sections/footer';
import { StandardCTA } from '@/components/standard-cta';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { getPostBySlug, posts, type BlogPost } from '../posts';
import { Calendar, Clock, Tag as TagIcon, Sparkles, Feather, ArrowUpRight } from 'lucide-react';

export default function BlogDetailPage(){
  const { slug } = useParams();
  const post = getPostBySlug(slug ?? '');

  if(!post){
    return (
      <div className="min-h-screen bg-[#FCF8F1]">
        <Header />
        <main>
          <section className="pt-40 pb-24">
            <div className="w-[90%] max-w-3xl mx-auto text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Article Not Found</h1>
              <p className="mt-4 text-gray-600">The blog post you are looking for does not exist or was moved.</p>
              <div className="mt-8">
                <Link to="/blog" className="px-6 py-3 rounded-xl bg-[#245E51] text-white font-semibold shadow hover:shadow-md transition">Back to Blog</Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    datePublished: post.date,
    author: { '@type': 'Organization', name: post.author.name },
    image: [post.image],
    keywords: post.tags?.join(', '),
  } as const;

  const keyInsights: string[] = post.sections
    .flatMap(section => section.bullets?.slice(0, 1) ?? [])
    .slice(0, 3);

  const relatedPosts: BlogPost[] = posts.filter(p => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FCF8F1]">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-36 sm:pt-44 lg:pt-52 pb-20 overflow-hidden">
          <div className="absolute inset-0 -z-10">
            <img src={post.image} alt="" className="w-full h-full object-cover opacity-30 blur-sm" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#FCF8F1] via-[#FCF8F1]/92 to-[#FCF8F1]" />
          </div>
          <motion.div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#A8FF51]/45 blur-3xl -z-10" animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.7, 0.5] }} transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }} />
          <motion.div className="absolute top-1/3 -left-24 w-64 h-64 rounded-full bg-[#245E51]/25 blur-3xl -z-10" animate={{ y: [0, -30, 0], opacity: [0.4, 0.6, 0.4] }} transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }} />
          <motion.div className="absolute bottom-10 right-1/4 w-48 h-48 rounded-full bg-white/50 blur-3xl -z-10" animate={{ y: [0, 28, 0], opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }} />

          <div className="w-[90%] max-w-5xl mx-auto">
            <div className="inline-flex items-center px-3 py-1 text-sm font-semibold text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30">
              Blog Insight
            </div>
            <motion.h1 initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ duration:.75, ease:[0.23,0.68,0.32,0.97] }} className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900">
              {post.title}
            </motion.h1>
            <motion.p initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:.8, delay:.08, ease:[0.23,0.68,0.32,0.97] }} className="mt-5 max-w-3xl text-lg sm:text-xl text-gray-700 leading-relaxed">
              {post.excerpt}
            </motion.p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-gray-600">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 border border-white/60 shadow-sm"><img src={post.author.avatar} alt={post.author.name} className="w-7 h-7 rounded-full" /><span className="font-medium text-gray-800">{post.author.name}</span></div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-white/50"><Calendar className="h-4 w-4" /><time dateTime={new Date(post.date).toISOString()}>{new Date(post.date).toLocaleDateString()}</time></div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-white/50"><Clock className="h-4 w-4" />{post.readingTime}</div>
            </div>
            {post.tags && post.tags.length > 0 && null}
          </div>
        </section>

        {/* Content */}
        <section className="py-10">
          <div className="w-[90%] max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
            <article className="lg:col-span-8 space-y-10">
              {keyInsights.length > 0 && (
                <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true, amount:.5 }} transition={{ duration:.6, ease:[0.23,0.68,0.32,0.97] }} className="rounded-3xl bg-gradient-to-br from-[#245E51]/90 to-[#0f362d] text-white shadow-xl border border-white/20 p-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold uppercase tracking-wide">
                    <Sparkles className="h-3.5 w-3.5" /> Key Takeaways
                  </div>
                  <h2 className="mt-4 text-2xl font-bold tracking-tight">What You'll Gain</h2>
                  <ul className="mt-5 space-y-3 text-sm sm:text-base text-white/85">
                    {keyInsights.map(point => (
                      <li key={point} className="flex items-start gap-3">
                        <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-xs font-semibold">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              <motion.div initial={{ opacity:0, y:22 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true, amount:.4 }} transition={{ duration:.55, ease:[0.23,0.68,0.32,0.97] }} className="rounded-3xl bg-white border border-gray-100 shadow-lg p-8">
                <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-widest text-[#245E51]">
                  <Feather className="h-4 w-4" /> Deep Dive
                </div>
                <p className="mt-5 text-lg text-gray-700 leading-relaxed">{post.excerpt}</p>
                <div className="mt-8 space-y-12">
                  {post.sections.map((s, i) => (
                    <motion.section key={s.id} id={s.id} initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true, amount:.4 }} transition={{ duration:.6, delay:i*0.03, ease:[0.23,0.68,0.32,0.97] }}>
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#245E51]/10 text-[#245E51] font-semibold text-sm">{(i+1).toString().padStart(2,'0')}</span>
                        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{s.title}</h2>
                      </div>
                      <div className="mt-4 space-y-4 text-gray-700 leading-relaxed">
                        {s.content.map((p) => (<p key={p}>{p}</p>))}
                      </div>
                      {s.bullets && (
                        <div className="mt-5 rounded-2xl bg-[#FCF8F1] border border-[#F0EADB] p-5">
                          <div className="flex items-center gap-2 text-sm font-semibold text-[#245E51]"><Sparkles className="h-4 w-4" /> Practical Steps</div>
                          <ul className="mt-3 space-y-2 text-sm text-gray-700 list-disc list-inside">
                            {s.bullets.map(b => (<li key={b}>{b}</li>))}
                          </ul>
                        </div>
                      )}
                    </motion.section>
                  ))}
                </div>
                <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
                  <Link to="/blog" className="inline-flex items-center gap-2 text-[#245E51] font-semibold hover:underline">← Back to all articles</Link>
                  <a href="/free-trial" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#245E51] text-white text-sm font-semibold shadow hover:shadow-md transition">
                    Book Free Trial <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </motion.div>

              {post.tags && post.tags.length > 0 && (
                <motion.div initial={{ opacity:0, y:18 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true, amount:.4 }} transition={{ duration:.6, ease:[0.23,0.68,0.32,0.97] }} className="rounded-2xl bg-white border border-gray-100 shadow-md p-6">
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
                    <TagIcon className="h-4 w-4 text-[#245E51]" /> Topics
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {post.tags.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-[#245E51]/10 border border-[#245E51]/20 text-[#245E51] font-semibold">
                        <Sparkles className="h-3.5 w-3.5" /> {t}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </article>

            {/* TOC */}
            <aside className="lg:col-span-4 lg:sticky lg:top-28 h-max space-y-6">
              <div className="rounded-2xl bg-white border border-gray-100 shadow-md p-6">
                <h3 className="font-semibold text-gray-900 tracking-tight">On this page</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {post.sections.map(s => (
                    <li key={s.id}>
                      <a href={`#${s.id}`} className="text-gray-700 hover:text-[#245E51]">{s.title}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-white border border-gray-100 shadow-md p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Free Trial Lesson</h4>
                <p className="text-sm text-gray-600">Experience our methodology with a personalized session.</p>
                <a href="/free-trial" className="mt-4 inline-flex px-4 py-2 rounded-lg bg-[#245E51] text-white text-sm font-semibold shadow hover:shadow-md transition">Start Free Trial</a>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-[#245E51] via-[#1b4b3f] to-[#0f2d25] text-white shadow-lg p-6">
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
                  <Sparkles className="h-4 w-4" /> Quick Insight
                </div>
                <p className="mt-3 text-sm text-white/85 leading-relaxed">Structured lessons, adaptive review and real-time feedback mirror elite tutoring experiences—tailored for Qur'an, Arabic and Islamic Studies.</p>
              </div>
            </aside>
          </div>
        </section>

        {/* Related */}
        {relatedPosts.length > 0 && (
          <section className="py-20">
            <div className="w-[90%] max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#245E51]/10 text-[#245E51] text-xs font-semibold uppercase tracking-wide">
                    <Sparkles className="h-3.5 w-3.5" /> Keep Exploring
                  </div>
                  <h2 className="mt-4 text-3xl font-bold text-gray-900 tracking-tight">Related Articles</h2>
                  <p className="mt-3 text-gray-600 max-w-2xl text-sm">Discover more guides on Qur'an memorization, tajwīd precision and Arabic fluency inspired by leading e-learning experiences.</p>
                </div>
                <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#245E51] hover:underline">View all posts <ArrowUpRight className="h-4 w-4" /></Link>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((related) => (
                  <motion.a
                    key={related.slug}
                    href={`/blog/${related.slug}`}
                    initial={{ opacity:0, y:24 }}
                    whileInView={{ opacity:1, y:0 }}
                    viewport={{ once:true, amount:.4 }}
                    transition={{ duration:.55, ease:[0.23,0.68,0.32,0.97] }}
                    className="group rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      <span className="absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/90 text-gray-800 border border-gray-200">
                        {related.category}
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-gray-900 tracking-tight group-hover:text-[#245E51] line-clamp-2">{related.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 line-clamp-3">{related.excerpt}</p>
                      <div className="mt-4 flex items-center gap-3 text-xs text-gray-500">
                        <time dateTime={new Date(related.date).toISOString()}>{new Date(related.date).toLocaleDateString()}</time>
                        <span>•</span>
                        <span>{related.readingTime}</span>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          </section>
        )}

        <StandardCTA />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      </main>
      <Footer />
    </div>
  );
}
